import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AlertTriangle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { PurchaseOrderSummary } from '../../../types/procurement.types';
import { purchaseOrdersApi } from '../../../utils/procurement-api';
import { daysAgo, money, orderReference, vendorName } from './procurement-ui';
import { cn } from '../../../components/ui/utils';

/** How long a request has been waiting — amber until the third day, red from then on. */
const AgeChip = ({ submittedAt }: { submittedAt: string | null }) => {
    const days = daysAgo(submittedAt);
    return (
        <span className={cn(
            'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-semibold',
            days >= 3
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-amber-300 bg-amber-50 text-amber-700'
        )}>
            {days} day{days === 1 ? '' : 's'} waiting
        </span>
    );
};

/**
 * Approvals queue — every PO sitting in `pending_approval`.
 *
 * Oldest first, because the whole point of the screen is the one that has been waiting
 * longest; the age chip turns red on day three so it is visible without counting.
 *
 * Approving here carries the whole decision: a note is optional (it lands on the PO),
 * but a rejection must say why, so the reason can never be empty.
 */
export default function PurchaseOrderApprovalsPage() {
    const ability = useAbility(AbilityContext);
    const canApprove = ability.can(ACTIONS.APPROVE, RESOURCES.PURCHASE_ORDER_APPROVAL);
    const navigate = useNavigate();

    const [orders, setOrders] = useState<PurchaseOrderSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [approveTarget, setApproveTarget] = useState<PurchaseOrderSummary | null>(null);
    const [approveNote, setApproveNote] = useState('');
    const [rejectTarget, setRejectTarget] = useState<PurchaseOrderSummary | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [actionError, setActionError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await purchaseOrdersApi.list({
                status: 'pending_approval',
                limit: '200',
            });
            setOrders([...data.orders].sort((a, b) =>
                (a.submittedAt ?? '').localeCompare(b.submittedAt ?? '')));
            setError(null);
        } catch (err) {
            // The api client has already toasted; the screen still explains itself.
            setError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load the approval queue');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => { if (canApprove) load(); }, [canApprove, load]);

    const closeDialogs = () => {
        setApproveTarget(null);
        setRejectTarget(null);
        setApproveNote('');
        setRejectReason('');
        setActionError(null);
    };

    const approve = async () => {
        if (!approveTarget) return;
        try {
            setBusy(true);
            setActionError(null);
            await purchaseOrdersApi.approve(approveTarget.id, approveNote.trim() || undefined);
            toast.success(`${orderReference(approveTarget)} approved`);
            closeDialogs();
            await load();
        } catch (err) {
            setActionError(err instanceof Error && err.message
                ? err.message
                : 'Could not approve this purchase order');
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        if (!rejectTarget || rejectReason.trim().length === 0) return;
        try {
            setBusy(true);
            setActionError(null);
            await purchaseOrdersApi.reject(rejectTarget.id, rejectReason.trim());
            toast.success(`${orderReference(rejectTarget)} rejected`);
            closeDialogs();
            await load();
        } catch (err) {
            setActionError(err instanceof Error && err.message
                ? err.message
                : 'Could not reject this purchase order');
        } finally {
            setBusy(false);
        }
    };

    if (!canApprove) return <AccessRestricted />;

    const openDetail = (order: PurchaseOrderSummary) =>
        navigate(`/stock/procurement/purchase-orders/${order.id}`);

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <div>
                <h1 className="text-2xl font-bold">Approvals</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Purchase orders over a branch's approval threshold, oldest first.
                </p>
            </div>

            {loading && !hasLoaded ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-20 md:h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : error && orders.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={28} className="mx-auto text-destructive mb-3" />
                        <p className="font-medium">Could not load the approval queue</p>
                        <p className="text-sm text-muted-foreground mt-1">{error}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            ) : orders.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <Check size={28} className="mx-auto text-emerald-600 mb-3" />
                        <p className="font-medium">Nothing awaiting approval</p>
                        <p className="text-sm text-muted-foreground mt-1">
                            Requests land here when their total crosses the branch threshold.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <p className="text-sm text-muted-foreground">
                        {orders.length} request{orders.length === 1 ? '' : 's'} waiting
                    </p>

                    {error && (
                        <p className="text-sm text-amber-700">
                            Showing the last queue we loaded — {error}
                        </p>
                    )}

                    {/* Mobile — decision buttons on the card itself */}
                    <div className="md:hidden space-y-3">
                        {orders.map((o) => (
                            <Card
                                key={o.id}
                                className="p-4 active:bg-accent/40"
                                onClick={() => openDetail(o)}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <span className="font-mono font-medium">{orderReference(o)}</span>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">
                                            {o.requester.name} · {vendorName(o.vendor)}
                                        </p>
                                        <p className="text-xs text-muted-foreground truncate">
                                            {o.branch.name}{o.project ? ` · ${o.project.name}` : ''}
                                        </p>
                                    </div>
                                    <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
                                        {money(o.totalValue)}
                                    </span>
                                </div>

                                <div className="mt-3">
                                    <AgeChip submittedAt={o.submittedAt} />
                                </div>

                                <div className="mt-3 flex gap-2">
                                    <Button
                                        size="sm"
                                        className="flex-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionError(null);
                                            setApproveTarget(o);
                                        }}
                                    >
                                        <Check size={14} />Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActionError(null);
                                            setRejectTarget(o);
                                        }}
                                    >
                                        <X size={14} />Reject
                                    </Button>
                                </div>
                            </Card>
                        ))}
                    </div>

                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Ref</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Requester</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Vendor</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Branch</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Project</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Total</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Waiting</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Decision</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {orders.map((o) => (
                                        <TableRow
                                            key={o.id}
                                            onClick={() => openDetail(o)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="font-mono whitespace-nowrap">
                                                {orderReference(o)}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">{o.requester.name}</TableCell>
                                            <TableCell>{vendorName(o.vendor)}</TableCell>
                                            <TableCell className="whitespace-nowrap">{o.branch.name}</TableCell>
                                            <TableCell>
                                                {o.project?.name ?? <span className="text-muted-foreground">–</span>}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums whitespace-nowrap">
                                                {money(o.totalValue)}
                                            </TableCell>
                                            <TableCell><AgeChip submittedAt={o.submittedAt} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActionError(null);
                                                            setApproveTarget(o);
                                                        }}
                                                    >
                                                        <Check size={14} />Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActionError(null);
                                                            setRejectTarget(o);
                                                        }}
                                                    >
                                                        <X size={14} />Reject
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <Dialog
                open={approveTarget !== null}
                onOpenChange={(open) => { if (!open) closeDialogs(); }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Approve {approveTarget ? orderReference(approveTarget) : ''}
                        </DialogTitle>
                        <DialogDescription>
                            The order moves to Approved and is ready to issue. The note is optional
                            and is recorded against the purchase order.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="approval-note">Note (optional)</Label>
                        <Textarea
                            id="approval-note"
                            value={approveNote}
                            onChange={(e) => setApproveNote(e.target.value)}
                            placeholder="Checked against the quote — proceed."
                            rows={3}
                        />
                    </div>
                    {actionError && <p className="text-sm text-destructive">{actionError}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialogs} disabled={busy}>Cancel</Button>
                        <Button onClick={approve} disabled={busy}>
                            {busy ? 'Approving…' : 'Approve'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={rejectTarget !== null}
                onOpenChange={(open) => { if (!open) closeDialogs(); }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Reject {rejectTarget ? orderReference(rejectTarget) : ''}
                        </DialogTitle>
                        <DialogDescription>
                            The requester sees this reason. A rejected purchase order can be
                            duplicated into a fresh draft once it has been corrected.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="rejection-reason">Reason</Label>
                        <Textarea
                            id="rejection-reason"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Price is above the agreed rate card."
                            rows={3}
                        />
                    </div>
                    {actionError && <p className="text-sm text-destructive">{actionError}</p>}
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialogs} disabled={busy}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={reject}
                            disabled={busy || rejectReason.trim().length === 0}
                        >
                            {busy ? 'Rejecting…' : 'Reject'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
